/**
 * The Details tab: the estimate's own reference facts in one key-value card —
 * its numbers, status, origin, who is on it, revision and dates. Empty values
 * are hidden; a fact whose absence matters says so. The header's key facts
 * repeat here on purpose: the header is the summary, this is the full list.
 * Line-level facts (catalogue item, the customer's product reference) are in
 * the line's ▸ on Lines.
 */
import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import {
  contactSourceLabel,
  estimateStatusLabel,
  expirySourceLabel,
  informationCoverageExplanation,
} from "@shared-core/domain/estimate-presentation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import {
  formatDateOnly,
  formatValidUntil,
  KeyValues,
  Section,
  TabContainer,
} from "./atoms";

export function DetailsTab({
  estimate,
  draft,
  origin,
}: {
  estimate: EstimateDetailViewItem;
  draft: MerchandiseDraftEstimate | null;
  /** The origin as the header's key facts word it. */
  origin: string;
}) {
  return (
    <TabContainer label="Details">
      {draft ? (
        <DraftDetails draft={draft} origin={origin} />
      ) : (
        <EstimateDetails estimate={estimate} origin={origin} />
      )}
    </TabContainer>
  );
}

/** A draft's own identifiers: it has no estimate number until it is sent. */
function DraftDetails({
  draft,
  origin,
}: {
  draft: MerchandiseDraftEstimate;
  origin: string;
}) {
  return (
    <Section title="Estimate details">
      <KeyValues
        rows={[
          { label: "Reference", value: draft.reference },
          { label: "Project", value: draft.request.projectName },
          { label: "Internal id", value: draft.id },
          { label: "Origin", value: origin },
          { label: "Created", value: formatDateTimeOrDash(draft.createdAt) },
        ]}
      />
    </Section>
  );
}

function EstimateDetails({
  estimate,
  origin,
}: {
  estimate: EstimateDetailViewItem;
  origin: string;
}) {
  const { information } = estimate;
  const coverage = informationCoverageExplanation(information.coverage);
  const estimateNumber = estimate.estimate_id || information.estimate_number;
  const revision = information.revision;
  return (
    <Section title="Estimate details" info={coverage}>
      {/* items-start: a short column keeps its own row spacing rather than
          stretching to the long one. */}
      <div className="grid items-start gap-x-10 gap-y-4 lg:grid-cols-2">
        <KeyValues
          rows={[
            {
              label: "Estimate number",
              value: estimateNumber,
              gap: "Not synced yet",
              info: estimateNumber
                ? null
                : "Tharstern has not given this estimate its number yet. It arrives with the next sync; until then the page is addressed by its internal id.",
            },
            {
              label: "Tharstern status",
              value:
                estimate.status === "open"
                  ? "Pending"
                  : estimateStatusLabel(estimate.status),
            },
            { label: "Internal id", value: estimate.id },
            {
              label: "Header reference",
              value: information.estimate_header_ref,
            },
            { label: "Origin", value: origin },
            {
              label: "Revision",
              value:
                revision?.is_revision == null
                  ? null
                  : revision.is_revision
                    ? "A revision of an earlier estimate"
                    : "The original estimate",
              info:
                revision?.revised_from_estimate_id != null
                  ? `Revised from estimate ${revision.revised_from_estimate_id}`
                  : null,
            },
            {
              label: "Revisions on this header",
              value: revision?.revision_count,
            },
            {
              label: "Current revision",
              value:
                revision?.is_current_revision == null
                  ? null
                  : revision.is_current_revision
                    ? "Yes"
                    : "No — a later revision exists",
            },
            {
              label: "Locked",
              value:
                (revision?.is_locked ?? estimate.is_locked)
                  ? "Locked"
                  : "Editable",
            },
            {
              label: "Estimate date",
              value: formatDateOnly(estimate.estimate_date),
            },
            {
              label: "Valid until",
              value: formatValidUntil(information.expiry),
              info: information.expiry
                ? expirySourceLabel(information.expiry.source)
                : null,
            },
            {
              label: "Required by",
              value: formatDateOnly(
                estimate.delivery_billing.delivery.required_by,
              ),
            },
            {
              label: "Created",
              value: information.created_at
                ? formatDateTimeOrDash(information.created_at)
                : null,
            },
            {
              label: "Last modified",
              value: information.last_modified
                ? formatDateTimeOrDash(information.last_modified)
                : null,
            },
          ]}
        />
        <KeyValues
          rows={[
            { label: "Sales rep", value: information.sales_rep },
            { label: "Estimator", value: information.estimator },
            {
              label: "CSR/AM",
              value: information.csr,
              info:
                information.csr && information.csr_is_customer_default
                  ? "The customer's standing CSR/AM, not set on this estimate"
                  : null,
            },
            {
              label: "Customer contact",
              value: information.customer_contact?.name,
              info: information.customer_contact?.source
                ? contactSourceLabel(information.customer_contact.source)
                : null,
            },
            {
              label: "Contact email",
              value: information.customer_contact?.email,
            },
            {
              label: "Contact phone",
              value: information.customer_contact?.phone,
            },
            { label: "Job type", value: information.job_type },
            { label: "Job family", value: information.job_family },
            { label: "Market", value: information.market },
            { label: "Production site", value: information.production_site },
            { label: "Repeat", value: information.repeat_flag },
            { label: "Ordered", value: information.ordered_flag },
          ]}
        />
      </div>
    </Section>
  );
}
